import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import HeroSlider from '../../components/feature/HeroSlider';
import SEOHead from '../../components/base/SEOHead';
import { generateLocalBusinessSchema, generateWebPageSchema, generateFAQSchema } from '../../utils/seo';

const HomePage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const faqs = [
    {
      question: '利用するにはどのような手続きが必要ですか？',
      answer: 'まずは市町村で受給者証の申請を行っていただきます。受給者証取得後、当事業所との利用契約を結んでいただければご利用開始となります。手続きについてもサポートいたしますので、お気軽にご相談ください。'
    },
    {
      question: '見学や体験利用はできますか？',
      answer: 'はい、随時見学を受け付けております。また、体験利用も可能です。事前にお電話でご予約をお取りください。お子様の様子を見ながら、サービス内容についてご説明いたします。'
    },
    {
      question: '送迎サービスはありますか？',
      answer: '学校やご自宅への送迎サービスを行っております。送迎範囲については岡山市内を中心としておりますが、詳細はお問い合わせください。安全運転を心がけ、お子様を大切にお預かりいたします。'
    },
    {
      question: '利用料金はどのくらいかかりますか？',
      answer: '障害児通所給付費の対象となるため、世帯収入に応じた負担上限月額が設定されます。多くの方が月額4,600円または37,200円の負担となります。詳しくは個別にご相談ください。'
    },
    {
      question: 'どのような療育プログラムがありますか？',
      answer: '個別支援計画に基づいた個別療育、集団での社会性訓練、学習支援、運動療育、創作活動など、お子様の発達段階や特性に応じた多様なプログラムを提供しています。'
    },
    {
      question: '保護者との連携はどのように行われますか？',
      answer: '定期的な面談や連絡帳でのやり取り、必要に応じて電話連絡を行います。また、保護者向けの勉強会や懇談会も開催し、ご家庭での支援方法についてもアドバイスいたします。'
    }
  ];

  // Generate structured data
  const structuredData = [
    generateLocalBusinessSchema(),
    generateWebPageSchema(
      'おかやま放課後等デイサービス｜岡山市北区の発達支援・療育',
      '岡山市北区の放課後等デイサービス。発達障害のお子様への専門的な療育・支援を提供。個別支援計画、集団療育、学習支援など充実したプログラムで成長をサポートします。',
      '/'
    ),
    generateFAQSchema(faqs)
  ];

  const services = [
    {
      icon: 'ri-heart-fill',
      title: '個別支援計画',
      description: 'お子様一人ひとりの特性に合わせた個別支援計画を作成し、きめ細やかな療育を提供します。',
      color: 'text-red-500',
      image: 'https://public.readdy.ai/ai/img_res/01c768dde772ccd175451335585020da.jpg'
    },
    {
      icon: 'ri-group-fill',
      title: '集団療育',
      description: '同年代のお子様との関わりを通じて、社会性やコミュニケーション能力を育みます。',
      color: 'text-blue-500',
      image: 'https://readdy.ai/api/search-image?query=Group%20of%20Japanese%20children%20playing%20together%20in%20colorful%20daycare%20center%2C%20social%20interaction%20activities%2C%20circle%20time%2C%20cooperative%20games%2C%20bright%20cheerful%20environment%2C%20natural%20lighting%2C%20inclusive%20atmosphere%2C%20children%20learning%20social%20skills%20together&width=600&height=400&seq=service2&orientation=landscape'
    },
    {
      icon: 'ri-book-fill',
      title: '学習支援',
      description: '学校の宿題サポートや基礎学習の定着を図り、学習習慣の確立をお手伝いします。',
      color: 'text-green-500',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20child%20studying%20with%20supportive%20teacher%2C%20homework%20help%20session%2C%20books%20and%20learning%20materials%20on%20desk%2C%20focused%20learning%20environment%2C%20bright%20study%20room%2C%20natural%20lighting%2C%20educational%20support%2C%20encouraging%20atmosphere&width=600&height=400&seq=service3&orientation=landscape'
    },
    {
      icon: 'ri-palette-fill',
      title: '創作活動',
      description: '工作や絵画などの創作活動を通じて、創造性や集中力、達成感を育てます。',
      color: 'text-purple-500',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20children%20doing%20arts%20and%20crafts%20activities%2C%20painting%20and%20creative%20projects%2C%20colorful%20art%20supplies%2C%20hands%20working%20on%20creative%20tasks%2C%20bright%20art%20room%2C%20natural%20lighting%2C%20joyful%20creative%20expression%2C%20finished%20artwork%20displayed&width=600&height=400&seq=service4&orientation=landscape'
    },
    {
      icon: 'ri-run-fill',
      title: '運動療育',
      description: '体を動かす楽しさを感じながら、基礎体力や運動能力の向上を図ります。',
      color: 'text-orange-500',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20children%20doing%20physical%20activities%20and%20exercises%20in%20bright%20gym%20room%2C%20movement%20therapy%2C%20fun%20exercise%20equipment%2C%20active%20play%2C%20energetic%20atmosphere%2C%20natural%20lighting%2C%20children%20enjoying%20physical%20activities%2C%20developmental%20motor%20skills%20training&width=600&height=400&seq=service5&orientation=landscape'
    },
    {
      icon: 'ri-home-heart-fill',
      title: '生活訓練',
      description: '日常生活に必要なスキルを身につけ、自立に向けた支援を行います。',
      color: 'text-pink-500',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20children%20learning%20daily%20life%20skills%2C%20practicing%20self-care%20activities%2C%20organized%20learning%20environment%2C%20life%20skills%20training%2C%20bright%20comfortable%20room%2C%20natural%20lighting%2C%20independence%20training%2C%20supportive%20teaching%20atmosphere&width=600&height=400&seq=service6&orientation=landscape'
    }
  ];

  const staff = [
    {
      name: '岡山 太郎',
      position: '管理者・児童発達支援管理責任者',
      experience: '療育経験15年',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Japanese%20male%20therapist%20in%20his%2040s%2C%20wearing%20casual%20business%20attire%2C%20warm%20smile%2C%20standing%20in%20a%20bright%20therapy%20room%2C%20confident%20posture%2C%20kind%20expression%2C%20natural%20lighting%2C%20modern%20interior%20background&width=300&height=400&seq=staff1&orientation=portrait',
      description: '長年の経験を活かし、お子様一人ひとりに最適な支援プログラムを提供します。'
    },
    {
      name: '田中 花子',
      position: '児童指導員',
      experience: '保育士資格・療育経験8年',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Japanese%20female%20therapist%20in%20her%2030s%2C%20wearing%20colorful%20casual%20clothing%2C%20gentle%20smile%2C%20sitting%20with%20educational%20toys%2C%20caring%20expression%2C%20bright%20daycare%20environment%2C%20natural%20lighting&width=300&height=400&seq=staff2&orientation=portrait',
      description: '保育士としての経験を生かし、お子様の心に寄り添った支援を心がけています。'
    },
    {
      name: '佐藤 次郎',
      position: '作業療法士',
      experience: '作業療法士・療育経験10年',
      image: 'https://readdy.ai/api/search-image?query=Modern%20Japanese%20male%20occupational%20therapist%20in%20his%20early%2030s%2C%20bright%20cheerful%20smile%2C%20wearing%20contemporary%20casual%20shirt%2C%20friendly%20approachable%20demeanor%2C%20bright%20modern%20therapy%20room%20setting%2C%20natural%20lighting%2C%20positive%20energy%2C%20professional%20yet%20relaxed%20atmosphere&width=300&height=400&seq=staff3new&orientation=portrait',
      description: '作業療法士の専門知識を活かし、お子様の発達段階に応じた療育を提供します。'
    }
  ];

  const testimonials = [
    {
      name: '山田様',
      child: '小学3年生の息子',
      image: 'https://readdy.ai/api/search-image?query=Happy%20Japanese%20mother%20in%20her%2030s%20with%20warm%20smile%2C%20casual%20clothing%2C%20sitting%20in%20comfortable%20living%20room%2C%20natural%20expression%2C%20bright%20interior%2C%20family%20photo%20atmosphere&width=200&height=200&seq=parent1&orientation=squarish',
      comment: 'スタッフの皆さんがとても親身になってくださり、息子も楽しく通っています。家でも積極的に話すようになりました。'
    },
    {
      name: '鈴木様',
      child: '小学1年生の娘',
      image: 'https://readdy.ai/api/search-image?query=Smiling%20Japanese%20father%20in%20his%2040s%2C%20wearing%20casual%20shirt%2C%20sitting%20at%20home%2C%20gentle%20expression%2C%20comfortable%20home%20environment%2C%20natural%20lighting%2C%20family%20setting&width=200&height=200&seq=parent2&orientation=squarish',
      comment: '個別支援計画のおかげで、娘の成長を実感できています。先生方の専門的なアドバイスがとても参考になります。'
    },
    {
      name: '高橋様',
      child: '小学5年生の息子',
      image: 'https://readdy.ai/api/search-image?query=Kind%20Japanese%20mother%20in%20her%2040s%2C%20wearing%20comfortable%20clothing%2C%20sitting%20in%20bright%20room%2C%20peaceful%20expression%2C%20home%20atmosphere%2C%20natural%20lighting%2C%20caring%20demeanor&width=200&height=200&seq=parent3&orientation=squarish',
      comment: '集団活動を通じて、お友達との関わり方が上手になりました。安心して預けることができます。'
    },
    {
      name: '伊藤様',
      child: '小学2年生の娘',
      image: 'https://readdy.ai/api/search-image?query=Cheerful%20Japanese%20mother%20in%20her%2030s%2C%20wearing%20bright%20colored%20top%2C%20sitting%20comfortably%2C%20happy%20expression%2C%20modern%20home%20interior%2C%20natural%20lighting%2C%20positive%20atmosphere&width=200&height=200&seq=parent4&orientation=squarish',
      comment: '学習支援のおかげで宿題に取り組む姿勢が変わりました。毎日楽しそうに通っているのが何より嬉しいです。'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="おかやま放課後等デイサービス｜岡山市北区の発達支援・療育"
        description="岡山市北区の放課後等デイサービス。発達障害のお子様への専門的な療育・支援を提供。個別支援計画、集団療育、学習支援など充実したプログラムで成長をサポートします。"
        keywords="岡山市,放課後等デイサービス,発達支援,療育,個別支援計画,集団療育,学習支援"
        canonicalUrl={`${siteUrl}/`}
        structuredData={structuredData}
      />
      
      <Header />
      
      {/* Hero Slider */}
      <HeroSlider />

      {/* Services Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>私たちのサービス</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              岡山市の放課後等デイサービスとして、お子様一人ひとりの個性と可能性を大切にした療育プログラムを提供しています。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <div className="w-full h-48 md:h-56">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <div className={`w-16 h-16 md:w-20 md:h-20 ${service.color} bg-opacity-10 rounded-full flex items-center justify-center mb-4 md:mb-6`}>
                    <i className={`${service.icon} text-2xl md:text-3xl ${service.color}`}></i>
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">{service.title}</h3>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed break-words">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Staff Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>スタッフ紹介</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              経験豊富な専門スタッフが、お子様の成長を全力でサポートいたします。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {staff.map((member, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <div className="w-full h-72 md:h-80">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-2">{member.name}</h3>
                  <p className="text-sm md:text-base text-blue-600 font-medium mb-2">{member.position}</p>
                  <p className="text-xs md:text-sm text-gray-500 mb-3">{member.experience}</p>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed break-words">{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>利用者様の声</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              実際にご利用いただいている保護者様からのお声をご紹介します。
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 md:p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-start gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover object-top flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center mb-2 gap-2">
                      <h4 className="font-semibold text-gray-800 text-base md:text-lg">{testimonial.name}</h4>
                      <span className="text-sm md:text-base text-gray-500 whitespace-nowrap">({testimonial.child})</span>
                    </div>
                    <div className="flex text-yellow-400 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-base md:text-lg"></i>
                      ))}
                    </div>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed break-words">{testimonial.comment}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>よくある質問</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              皆様からよくいただくご質問にお答えします。
            </p>
          </div>
          <div className="space-y-4 md:space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl shadow-md hover:shadow-xl transition-all duration-300">
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer p-6 md:p-8 hover:bg-blue-100 rounded-xl transition-colors">
                    <h3 className="text-base md:text-lg font-semibold text-gray-800 pr-4 break-words flex-1">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-xl md:text-2xl text-gray-500 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-6 md:px-8 pb-6 md:pb-8">
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed break-words">{faq.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-16 md:py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>お問い合わせ</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              見学のご予約やご質問など、お気軽にお問い合わせください。
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-xl p-6 md:p-10">
            <form 
              action="https://readdy.ai/api/form/d464rkfjsic9gsa4tvig" 
              method="POST" 
              data-readdy-form
              id="contact-form-main"
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base transition-all"
                    placeholder="山田 太郎"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base transition-all"
                    placeholder="086-012-3456"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base transition-all"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="child-age" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
                  お子様の年齢
                </label>
                <select
                  id="child-age"
                  name="child-age"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base transition-all cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="6歳">6歳</option>
                  <option value="7歳">7歳</option>
                  <option value="8歳">8歳</option>
                  <option value="9歳">9歳</option>
                  <option value="10歳">10歳</option>
                  <option value="11歳">11歳</option>
                  <option value="12歳">12歳</option>
                  <option value="13歳">13歳</option>
                  <option value="14歳">14歳</option>
                  <option value="15歳">15歳</option>
                  <option value="16歳">16歳</option>
                  <option value="17歳">17歳</option>
                  <option value="18歳">18歳</option>
                </select>
              </div>

              <div>
                <label htmlFor="inquiry-type" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <select
                  id="inquiry-type"
                  name="inquiry-type"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base transition-all cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="見学希望">見学希望</option>
                  <option value="体験利用希望">体験利用希望</option>
                  <option value="利用相談">利用相談</option>
                  <option value="サービス内容について">サービス内容について</option>
                  <option value="料金について">料金について</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
                  詳細・ご質問内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base resize-vertical transition-all"
                  placeholder="ご質問やご相談内容をお聞かせください（500文字以内）"
                ></textarea>
                <p className="text-xs md:text-sm text-gray-500 mt-2">500文字以内でご入力ください</p>
              </div>

              <div className="text-center pt-4">
                <button
                  type="submit"
                  className="inline-flex items-center justify-center px-8 md:px-10 py-4 bg-blue-600 text-white text-base md:text-lg font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
                >
                  <i className="ri-send-plane-fill mr-2"></i>
                  送信する
                </button>
              </div>
            </form>
          </div>

          <div className="mt-10 text-center">
            <p className="text-base md:text-lg text-gray-600 mb-4">お急ぎの方はお電話でお問い合わせください</p>
            <a
              href="tel:086-012-3456"
              className="inline-flex items-center justify-center px-8 md:px-10 py-4 bg-green-600 text-white text-base md:text-lg font-semibold rounded-lg hover:bg-green-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
            >
              <i className="ri-phone-fill mr-2"></i>
              086-012-3456
            </a>
            <p className="text-sm md:text-base text-gray-500 mt-3">営業時間: 9:00 〜 18:00（日曜・祝日除く）</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
